import { apiClient } from '../../../services/apiClient';
import type { ScenarioResponse } from './types';
import { SCENARIO_JOB_STORAGE_KEY } from './constants';
import { waitForNextPoll } from '../../../utils/polling';
import { isCharacterActor, normalizeEthnicitySegment, type EthnicitySegment } from '../../../features/actors/actorAttributes';
import type { CastingActor, CastingSelectionState } from '../state/useGenerationState';
import { DEFAULT_VIDEO_MODEL } from '../state/modelCatalog';
import {
  deriveSceneVideoInputMode,
  deriveProductionMode,
  derivePerSceneDefaultMode,
  deriveEffectiveVideoGenerationMode,
  type SceneVideoInputModeScenarioSource,
  type SceneVideoInputModeSceneSource,
  type EffectiveVideoGenerationMode,
  type SceneVideoInputMode,
  type ExtendBlockingInputMode,
  extendBlockedByPrevious,
  extendBlockedReasonText,
} from '../../../features/scenarios/productionMode';

// スタイル×動画モデルに応じたキャスト送信の許可マトリクス (P5)。
export interface CastPayloadStyleContext {
  /** 選択中の動画モデルが Seedance 系か (videoSettings.videoModel?.startsWith('seedance'))。 */
  isSeedanceModel: boolean;
  /** 選択中の映像スタイルが非写実系か (visualStyleIsStylized)。 */
  isStylized: boolean;
  /** 「人物・キャラクターを含めない」が ON か。 */
  excludePeople: boolean;
}

// シナリオ生成リクエストの casting / casting_models (および shellGenerationSnapshotRef.
// castingModels) へ実際に送るキャストを、スタイル×動画モデルで絞り込む
// (docs/features/actor-character-support-plan.md §4 P5-3。CastingSection.tsx のゲートと
// 整合させる必要があるため純粋関数として抽出しテストする)。
//   - excludePeople: 常に空 (何を選んでいても送らない)
//   - Seedance: キャラクター俳優のみ。スタイルは問わない (CastingSection のキャラクター俳優
//     ピッカーは isStylized でも常時表示される。プリセットとは排他で別フィールドに送る)
//   - 非Seedance + 非写実系: キャラクター俳優のみ (人間俳優は非写実系では選択自体できない
//     不変条件があるが、fail-safe として送信直前でも同じ判定で絞り込む)
//   - 非Seedance + 実写系: 全俳優 (人間・キャラクターとも、従来どおり)
export function resolveCastPayloadActors<T extends { ethnicity?: string | null }>(
  fullCast: T[],
  { isSeedanceModel, isStylized, excludePeople }: CastPayloadStyleContext,
): T[] {
  if (excludePeople || fullCast.length === 0) return [];
  if (isSeedanceModel || isStylized) return fullCast.filter(isCharacterActor);
  return fullCast;
}

// Seedance プリセット (BytePlus・seedance_preset_actor 系フィールド) を送るか。
// プリセットは写実前提のため非写実系スタイルでは選べない (CastingSection がピッカー自体を
// 隠す)。excludePeople でも送らない。キャラクター俳優 (resolveCastPayloadActors) とは独立
// した判定で、Seedance かどうかに関わらず isSeedanceModel を満たさなければ常に false。
export function shouldSendSeedancePresetActors(
  { isSeedanceModel, isStylized, excludePeople }: CastPayloadStyleContext,
): boolean {
  return isSeedanceModel && !isStylized && !excludePeople;
}

// 「人物画像を添付」トグル (videoSettings.includeCastingSheet) の除外判定 (修正3-b・
// docs/features/character-casting-consistency-plan.md)。
//   - キャラクター俳優 (ethnicity='character') のキャラクターシートはトグルの対象外とし、
//     OFF でも添付候補に残す (「トグルの対象外」であって「無条件でプロバイダへ送信」
//     ではない。実際の添付可否は参照上限の切り詰めと BE の face-safe 検査が最終判定する)。
//   - 人間俳優のキャラクターシートは従来どおり includeCastingSheet===false で除外する。
export function filterCastSheetEntriesByToggle<T extends { isCharacter: boolean }>(
  entries: T[],
  includeCastingSheet: boolean,
): T[] {
  return entries.filter((entry) => entry.isCharacter || includeCastingSheet);
}

// 「人物画像を添付」トグルの表示条件 (修正3-b)。フルキャスト (主 + 追加俳優) に
// 人間俳優が1人もいない (キャラクター俳優のみ、またはキャスト空) 場合、
// filterCastSheetEntriesByToggle によりトグルは何にも効かない死にスイッチになるため
// 非表示にする。人間俳優が1人でもいれば従来どおり表示する。
export function shouldShowCastingSheetToggle<T extends { ethnicity?: string | null }>(
  fullCast: T[],
): boolean {
  return fullCast.some((actor) => !isCharacterActor(actor));
}

/** {@link buildSceneSettingsPayload} の入力。 */
export interface BuildSceneSettingsPayloadParams {
  /** videoGenerationMode === 'storyboard' か。 */
  isStoryboardMode: boolean;
  /** castModelIdsForModel(isSeedanceModel) の結果 (非写実系/Seedanceでの人間俳優除外済み)。 */
  castModelIds: string[];
  isSeedanceModel: boolean;
  isStylized: boolean;
  /** Seedance プリセット俳優 ID (フルキャスト分)。 */
  presetActorIds: string[];
}

// ストーリーボード動画 payload の settings 構築 (docs/features/
// character-casting-consistency-plan.md 修正2-b「payload 契約」)。
//   - storyboard モード: settings.model_ids を常に送る。キャストなし (未選択・
//     ユーザーの明示的解除・非写実系/Seedanceでの人間俳優除外いずれも含む) は [] を
//     明示送信する。settings オブジェクト自体も常に付与する (undefined を返さない)。
//     これにより BE の旧クライアント向けフォールバック (修正2-c、別タスク) は
//     「model_ids と単数 model がともに欠落」の旧クライアント・非標準経路のみに
//     限定される。
//   - storyboard 以外のモード: 従来どおり、キャストや Seedance プリセットが 1 件以上
//     あるときだけ settings を送る (空なら undefined を返し、payload から省略する)。
export function buildSceneSettingsPayload(
  params: BuildSceneSettingsPayloadParams,
): Record<string, unknown> | undefined {
  const { isStoryboardMode, castModelIds, isSeedanceModel, isStylized, presetActorIds } = params;
  const settings: Record<string, unknown> = {};
  if (isStoryboardMode) {
    // 複数AI俳優: model_ids[] が正。単数 model は後方互換ミラー (= [0])。
    settings.model_ids = castModelIds;
    if (castModelIds.length > 0) {
      settings.model = castModelIds[0];
    }
  } else if (castModelIds.length > 0) {
    settings.model_ids = castModelIds;
    settings.model = castModelIds[0];
  }
  if (isSeedanceModel && !isStylized && presetActorIds.length > 0) {
    settings.seedance_preset_actor_ids = presetActorIds;
    settings.seedance_preset_actor_id = presetActorIds[0];
  }
  return isStoryboardMode || Object.keys(settings).length > 0 ? settings : undefined;
}

/** 非Seedance→Seedanceへの動画モデル切替でフルキャストを俳優種別ごとに分ける結果。 */
export interface SeedanceSwitchCastSplit<T> {
  /** キャラクター俳優 (順序維持)。Seedance でも引き続き使えるため選択を維持する。 */
  keepCast: T[];
  /** 人間俳優 (順序維持)。Seedance では使えないため解除対象になる。 */
  clearedCast: T[];
  /** 人間俳優が1体でも含まれ、解除確認ダイアログを出す必要があるか。 */
  needsConfirm: boolean;
}

// 非Seedance→Seedanceへの動画モデル切替時、フルキャスト (主 + 追加俳優) を俳優種別で
// 分割する (docs/features/actor-character-support-plan.md §4 P5。レビュー指摘 P2:
// useScenarioConfirmDialogs.ts の切替確認が俳優種別を区別せず全解除していた)。
//   - キャラクター俳優のみ選択中: needsConfirm=false (確認不要・選択維持)
//   - 人間俳優のみ選択中: needsConfirm=true・keepCast=[] (従来どおり全解除)
//   - 混成: needsConfirm=true・keepCast にキャラクター俳優だけが残る (人間俳優のみ解除)
// filter は元配列の順序を保つため、keepCast の並びは「フルキャスト順 (登場人物A/B…)」から
// 人間俳優を除いたものと一致する。呼び出し側はこの keepCast の先頭を主俳優へ昇格させる。
export function splitCastForSeedanceSwitch<T extends { ethnicity?: string | null }>(
  fullCast: T[],
): SeedanceSwitchCastSplit<T> {
  const keepCast = fullCast.filter(isCharacterActor);
  const clearedCast = fullCast.filter((actor) => !isCharacterActor(actor));
  return { keepCast, clearedCast, needsConfirm: clearedCast.length > 0 };
}

// キャスティングセクションの AI俳優一覧 (人種フィルタ + 年代フィルタ) の絞り込み
// (docs/features/actor-character-support-plan.md フォローアップ・タスク3)。
//   - 人種フィルタ: 'none' 以外なら ethnicity の完全一致。キャラクター俳優
//     (ethnicity='character') も他の俳優と同じ完全一致で扱う (gender フィルタと同じ
//     ルール。「人種の軸に当てはまらないから常に残す」という usePromptModels /
//     list_models 側の既定フィルタ向け救済ロジックとは別物)。
//   - 年代フィルタ: キャラクター俳優は年齢を持たないため、年代フィルタが有効な間は
//     「非該当」として除外する (ExclusiveActorsPage の filterActors と同じ規約)。
export function filterCastingModelsForDisplay<
  T extends { ethnicity?: string | null },
>(
  models: T[] | undefined,
  ethnicityFilter: string,
  ageFilter: number | null,
): T[] | undefined {
  if (!models) return models;
  let result = models;
  if (ethnicityFilter && ethnicityFilter !== 'none') {
    result = result.filter((m) => (m.ethnicity ?? null) === ethnicityFilter);
  }
  if (ageFilter !== null) {
    result = result.filter((m) => !isCharacterActor(m));
  }
  return result;
}

/** `?model=` 事前選択 (キャスティング一覧「動画にキャスティング」からの遷移) の対象俳優。 */
export interface CastingParamTarget {
  ethnicity?: string | null;
  gender?: string | null;
  min_age?: number | null;
  max_age?: number | null;
}

/** `?model=` 処理直前のキャスティングフィルター・動画モデルの現在値。 */
export interface CastingParamCurrentState {
  /** 選択中の動画モデル (videoSettings.videoModel)。 */
  videoModel?: string | null;
  /** キャスティング性別フィルターの現在値 ('none' | 'male' | 'female' 等)。 */
  genderFilter: string;
  /** キャスティング年代フィルターの現在値。 */
  ageFilter: number | null;
  /** 「人物・キャラクターを含めない」の現在値。 */
  excludePeople: boolean;
}

/** `?model=` 処理で適用すべき state 変更のプラン。 */
export interface CastingParamPlan {
  /** setCastingEthnicityFilter に渡す値。 */
  ethnicityFilter: EthnicitySegment;
  /** true なら性別フィルターを 'none' にリセットする。 */
  resetGenderFilter: boolean;
  /** true なら年代フィルターを null にリセットする。 */
  resetAgeFilter: boolean;
  /** true なら Seedance→非Seedance の強制切替を行う (人間俳優のみ)。 */
  switchModelFromSeedance: boolean;
  /** true なら excludePeople を false に自動解除する。 */
  resetExcludePeople: boolean;
}

// AI俳優一覧「動画にキャスティング」(?model=) 遷移時の分岐ロジック
// (docs/features/character-casting-consistency-plan.md 修正1-a)。
//   - キャラクター俳優: Seedance 強制切替はスキップしてモデルを維持し、
//     人種セグメントは 'character'、年代・性別フィルターは無条件でリセットする
//     (フィルターが残っているとキャラクター俳優が一覧から除外され続けるため)。
//   - 人間俳優: 従来どおり Seedance からの強制切替を維持する。人種セグメントは
//     ethnicity を正規化して反映し、年代・性別フィルターは俳優と矛盾する
//     (一覧に出なくなる) 場合だけリセットする。
export function planCastingParamSelection(
  target: CastingParamTarget,
  current: CastingParamCurrentState,
): CastingParamPlan {
  if (isCharacterActor(target)) {
    return {
      ethnicityFilter: 'character',
      resetGenderFilter: true,
      resetAgeFilter: true,
      switchModelFromSeedance: false,
      resetExcludePeople: current.excludePeople,
    };
  }

  const genderMismatch = current.genderFilter !== 'none'
    && !!target.gender
    && current.genderFilter !== target.gender;
  const ageMismatch = current.ageFilter !== null && (
    (typeof target.min_age === 'number' && current.ageFilter < target.min_age)
    || (typeof target.max_age === 'number' && current.ageFilter > target.max_age)
  );

  return {
    ethnicityFilter: normalizeEthnicitySegment(target.ethnicity),
    resetGenderFilter: genderMismatch,
    resetAgeFilter: ageMismatch,
    switchModelFromSeedance: !!current.videoModel?.startsWith('seedance'),
    resetExcludePeople: current.excludePeople,
  };
}

// pendingDeepLinkCastScenarioId (settingsContext) にセットする「次に開く任意のシナリオへ
// 一度だけ適用する」ことを表すセンチネル値 (docs/features/
// character-casting-consistency-plan.md 修正2-a「修正1との相互作用」精緻化・
// 2026-08-20 実装レビュー P1×2)。具体的なシナリオIDと衝突しなければ値は何でもよい。
export const PENDING_NEXT_SCENARIO = '__pending_next_scenario__' as const;

// ?model= 処理時に pendingDeepLinkCastScenarioId へセットすべき値を決める
// (修正2-a「修正1との相互作用」精緻化)。boolean の一回限りフラグでは「どのシナリオに
// 適用する pending か」を表現できず、シナリオ表示中 (hydration 済み) に ?model= が処理
// されると消費されないまま残り、次のシナリオ切替で持ち越されてしまう (2度目のレビュー P1)。
//   - activeScenarioId あり (シナリオ表示中にその場適用済み) → pending を立てない (null)
//   - activeScenarioId なし (シナリオ未選択) → 次に開くシナリオへ一度だけ適用する
//     PENDING_NEXT_SCENARIO をセットする
export function resolveDeepLinkCastPendingTarget(activeScenarioId: string | null): string | null {
  return activeScenarioId ? null : PENDING_NEXT_SCENARIO;
}

// ---------------------------------------------------------------------------
// シナリオ DB キャスト (scenarios.casting_model_ids) の FE 復元 (修正2-a)。
// 判定ロジックのみを純粋関数として切り出し、useCastingHydration.ts から呼ぶ
// (docs/features/character-casting-consistency-plan.md 修正2-a・hydration ガード)。
// ---------------------------------------------------------------------------

/** アクティブシナリオが変わったときに hydration をどう扱うか。 */
export type CastingHydrationAction =
  /** 何もしない (シナリオ未選択 or 既に処理済みのシナリオID)。 */
  | 'noop'
  /** ローカルキャストを保持し、DB からの復元は行わない
   *  (?model= ディープリンク等で explicit なキャストを積んだ直後にシナリオを開いた場合)。 */
  | 'preserve'
  /** ローカルキャストをクリアし、DB (casting_model_ids) から inherited として復元する。 */
  | 'hydrate';

/**
 * hydration ガードのルール 1・3 (シナリオIDごとに初回1回だけ / シナリオ切替時のみ
 * クリア+inherited hydration) と、修正1 (ディープリンク) との相互作用の判定
 * (2026-08-20 実装レビュー P1×2 で精緻化)。
 *
 * preserve (DB 復元をスキップしローカルキャストを保持) は「pendingDeepLinkCastScenarioId
 * が PENDING_NEXT_SCENARIO または今回の activeScenarioId と一致していて、かつローカル
 * キャストがある」場合のみ発生する。boolean の一回限りフラグでは「どのシナリオに適用する
 * pending か」を表現できず、シナリオ表示中 (hydration 済み) に ?model= が処理されると
 * 消費されないまま残り、次のシナリオ切替で持ち越されてしまうため、適用対象シナリオID
 * (またはセンチネル) で管理する。単に castingSelectionState==='explicit' だけで preserve
 * すると、シナリオ A で通常編集 (explicit) したキャストが B へ持ち越されてしまう
 * (受け入れ条件「A のキャストを B へ持ち越さない」違反)。それ以外のシナリオ切替は、
 * 以前の状態が explicit でも必ず新シナリオの DB 値で hydrate する。
 *
 * preserve/hydrate いずれを確定した場合も、呼び出し側 (useCastingHydration) は pending
 * が残っていれば consumePendingDeepLinkCast() で消費すること (対象と異なるシナリオへ
 * 切り替えた時点で pending は無効になるため。残すと後続切替で誤発動する)。
 *
 * 同一シナリオ内のクエリ再取得で explicit 編集を上書きしないルール (ルール2) は
 * このシナリオ切替判定とは別に shouldApplyCastingHydrationResult 側でそのまま維持する。
 */
export function planCastingHydrationOnScenarioChange(params: {
  activeScenarioId: string | null;
  /** 直近に hydration 処理を終えた (hydrate または preserve を確定した) シナリオID。 */
  handledScenarioId: string | null;
  hasLocalCast: boolean;
  /** ?model= ディープリンクで積んだキャストの適用対象 (null=なし・PENDING_NEXT_SCENARIO=
   *  次に開く任意のシナリオ・具体的なシナリオID=そのシナリオへの適用待ち)。 */
  pendingDeepLinkCastScenarioId: string | null;
}): CastingHydrationAction {
  const { activeScenarioId, handledScenarioId, hasLocalCast, pendingDeepLinkCastScenarioId } = params;
  if (!activeScenarioId) return 'noop'; // シナリオを閉じて setup に戻る場合は何もしない (現行キャストを維持)
  if (activeScenarioId === handledScenarioId) return 'noop'; // 既に処理済み (ルール1)
  const pendingMatchesThisScenario = pendingDeepLinkCastScenarioId === PENDING_NEXT_SCENARIO
    || pendingDeepLinkCastScenarioId === activeScenarioId;
  if (hasLocalCast && pendingMatchesThisScenario) return 'preserve';
  return 'hydrate';
}

/** casting_model_ids 解決クエリ (usePromptModels({ ids })) の結果の正規化。 */
export type CastingHydrationOutcome<T> =
  /** シナリオに casting_model_ids が無い (復元対象なし。即完了扱い)。 */
  | { kind: 'no-cast' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
    kind: 'resolved';
    /** casting_model_ids の順序で解決できた俳優のみを並べたもの (ルール5)。 */
    actors: T[];
    /** 権限外・削除済みなどで解決できなかった ID (ルール6の警告用)。 */
    missingIds: string[];
  };

/**
 * casting_model_ids と usePromptModels({ ids: casting_model_ids }) の結果から、
 * 復元すべき俳優一覧を casting_model_ids の順序で並べ直して返す (ガードのルール5・6)。
 * usePromptModels の返却順は保証されないため、必ず Map 引きで並べ直す。
 */
export function resolveCastingHydrationOutcome<T extends { id: string }>(
  castingModelIds: string[],
  query: { data: T[] | undefined; isError: boolean },
): CastingHydrationOutcome<T> {
  if (castingModelIds.length === 0) return { kind: 'no-cast' };
  if (query.isError) return { kind: 'error' };
  if (!query.data) return { kind: 'loading' };

  const byId = new Map(query.data.map((model) => [model.id, model]));
  const actors: T[] = [];
  const missingIds: string[] = [];
  for (const id of castingModelIds) {
    const found = byId.get(id);
    if (found) actors.push(found);
    else missingIds.push(id);
  }
  return { kind: 'resolved', actors, missingIds };
}

/**
 * 解決クエリ完了時に、その結果を実際にローカルキャストへ適用してよいかの判定
 * (ガードのルール2・4)。
 *   - ルール4: hydration 開始時点のシナリオIDと現在のアクティブシナリオIDが異なる
 *     (= 遅延して返ってきた古いシナリオの結果) 場合は適用しない。
 *   - ルール2: 同じシナリオIDでも、待っている間にユーザーが編集して explicit に
 *     なっていた場合は適用しない (DB 値での上書き防止)。
 */
export function shouldApplyCastingHydrationResult(params: {
  requestedScenarioId: string;
  activeScenarioId: string | null;
  castingSelectionState: CastingSelectionState;
}): boolean {
  return params.requestedScenarioId === params.activeScenarioId
    && params.castingSelectionState !== 'explicit';
}

/** usePromptModels (PromptModel) の解決結果を CastingActor 形状へ変換する。
 *  CastingSection.tsx の toCastingActor と同じフィールド対応 (id/display_name/
 *  face_image_public_url/character_sheet_public_url/gender/min_age/max_age/ethnicity)。 */
export function promptModelToCastingActor(model: {
  id: string;
  display_name: string;
  face_image_public_url?: string;
  character_sheet_public_url?: string;
  gender?: string;
  min_age?: number;
  max_age?: number;
  ethnicity?: string;
}): CastingActor {
  return {
    id: model.id,
    display_name: model.display_name,
    face_image_public_url: model.face_image_public_url,
    character_sheet_public_url: model.character_sheet_public_url,
    gender: model.gender,
    min_age: model.min_age,
    max_age: model.max_age,
    ethnicity: model.ethnicity,
  };
}

// 登場人物数(character_count)から AI俳優の選択上限(人数)を導出する。
// 複数AI俳優対応 (docs/features/multi-ai-actor-support-plan.md): character_count を厳格上限とし、
// 未指定 (空) のときは従来どおり 1 人まで。値はオプションキー(cc_1 等)なので、まず対応する
// option のラベルから数字を拾い、無ければキー末尾の数字、いずれも無ければ 1 にフォールバック。
export function castingCapFromCharacterCount(
  characterCount: string,
  options: { value: string; label: string }[],
): number {
  if (!characterCount) return 1;
  const opt = options.find((o) => o.value === characterCount);
  const fromLabel = opt?.label.match(/\d+/)?.[0];
  if (fromLabel) return Math.max(1, parseInt(fromLabel, 10));
  const fromKey = characterCount.match(/\d+/)?.[0];
  if (fromKey) return Math.max(1, parseInt(fromKey, 10));
  return 1;
}

// 選択中の動画モデルが受け入れられる俳優キャスト数の上限。
// キャラシ型AI俳優も Seedance プリセット (asset://) も参照画像枠を1人1枚消費するため、
// 参照枠の小さいモデルではキャスト数を人数 (character_count) より先に制限する必要がある。
// 正本は generation_models.metadata.max_cast_actors (DB 駆動)。未設定 (行なし・キーなし・
// 一覧未ロード) は MiniMax (画像5枠を商品タイル/ロゴと分け合う) のみ 3、他は無制限。
// 呼び出し側で castingCapFromCharacterCount と min を取って実効上限にする。
export function modelCastActorCap(
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): number {
  const row = videoModels?.find((m) => m.model_code === videoModel);
  const cap = row?.metadata?.max_cast_actors;
  if (typeof cap === 'number' && Number.isFinite(cap) && cap >= 1) return Math.floor(cap);
  return (videoModel ?? '').startsWith('minimax') ? 3 : Number.POSITIVE_INFINITY;
}

// フレームモードで対応するアスペクト比かチェック
export function isCompatibleFrameAspectRatio(asset: { sourceUrl?: string; width?: number | null; height?: number | null }): boolean {
  let aspectRatio: number | null = null;

  if (asset.width && asset.height) {
    aspectRatio = asset.width / asset.height;
  }

  if (!aspectRatio) return false;

  const compatibleRatios = [
    16 / 9,   // 16:9
    9 / 16,   // 9:16
    7 / 4,    // 7:4
    4 / 7,    // 4:7
  ];

  const tolerance = 0.05;

  return compatibleRatios.some(ratio =>
    Math.abs(aspectRatio! - ratio) / ratio < tolerance
  );
}

/** ランダム生成のスケルトンシェル (生成完了を待たず早期遷移する単位)。 */
export interface ShellScenario {
  id: string;
  model: string;
  /** 全編モーションの BGM 先行フロー (§10.5) のシェルだけ。プレースホルダーの generation_metadata に写す */
  motion?: {
    bgmMode: 'generate' | 'upload' | 'none';
    autoContinue: boolean;
    scenarioJobId: string | null;
    song?: unknown;
  };
}

/** requestGenerateScenario の戻り値。
 *  - 通常: ジョブ完了まで poll した最終 ScenarioResponse。
 *  - ランダム(シェル): ジョブ完了を待たず、早期遷移用の shell 情報を返す。
 */
export type GenerateScenarioOutcome =
  | ScenarioResponse
  | { __shellFlow: true; jobId: string; shellScenarios: ShellScenario[] };

export function isShellFlowOutcome(
  o: GenerateScenarioOutcome,
): o is { __shellFlow: true; jobId: string; shellScenarios: ShellScenario[] } {
  return (o as { __shellFlow?: boolean }).__shellFlow === true;
}

// このタブのメモリ内で「どこかのマウントが追跡中」のシナリオ生成ジョブ ID。
// requestGenerateScenario で登録し、pollScenarioJob の完了/失敗/タイムアウト、または
// シェルフローの全シェル確定 (useScenarioGeneration の settleShell) で解除する。
// sessionStorage の SCENARIO_JOB_STORAGE_KEY は「リロード跨ぎの復帰用」で、メモリ内に
// 追跡者がいるかは表せない。同一タブ内の再マウント (別画面から戻る等) で復帰 effect が
// 同じジョブを二重ポーリングしないよう、この registry で判定する。
const inFlightScenarioJobIds = new Set<string>();

export function isScenarioJobInFlight(jobId: string): boolean {
  return inFlightScenarioJobIds.has(jobId);
}

/** ジョブの追跡 (ポーリング) を終了する: registry から外す。復帰用キーは消さない。
 *  キーは「結果がマウント中のインスタンスに消費された」時点で
 *  clearScenarioJobResumeKeyIfSettled で消す (アンマウント中に完了した結果は
 *  useScenarioGeneration が保留し、次回マウントで消費するまでキーを残す)。 */
export function settleScenarioJob(jobId: string): void {
  inFlightScenarioJobIds.delete(jobId);
}

/** 復帰用キーのジョブが追跡終了済みなら、キーを消す (結果の消費側・失敗側から呼ぶ)。 */
export function clearScenarioJobResumeKeyIfSettled(): void {
  try {
    const jobId = sessionStorage.getItem(SCENARIO_JOB_STORAGE_KEY);
    if (jobId && !inFlightScenarioJobIds.has(jobId)) {
      sessionStorage.removeItem(SCENARIO_JOB_STORAGE_KEY);
    }
  } catch { /* ignore */ }
}

/** 台本ファイル解析 API のレスポンス (docs/features/scenario-script-import-plan.md §6.1)。 */
export interface ParseScriptFileResponse {
  text: string;
  source_format: 'markdown' | 'text' | 'docx';
  char_count: number;
  warnings: string[];
}

/**
 * 台本ファイル (.md / .txt) をテキストに変換する (LLM 不使用・ファイルは保存されない)。
 * 400 は `{ error, error_code? }`。呼び出し側で getResponseErrorMessage してトーストに出す。
 */
export async function requestParseScriptFile(file: File): Promise<ParseScriptFileResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<ParseScriptFileResponse>(
    '/generation/scenarios/script-import/parse',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function requestGenerateScenario(
  payload: Record<string, unknown>,
  opts?: {
    /** POST 応答で job_id が確定した時点で呼ぶ。**false を返すとこのジョブを追跡しない**
     *  = リロード復帰用キーにも in-flight registry にも載せない。
     *  (呼び出し側が「応答より先に切り離された」と判断したときに使う。utils は track を
     *   知らないので、判断はコールバック側で行う) */
    onJobCreated?: (jobId: string) => boolean | void;
  },
): Promise<GenerateScenarioOutcome> {
  const { data: jobData } = await apiClient.post<{ job_id: string; shell_scenarios?: ShellScenario[] }>(
    '/generation/scenarios/generate-async',
    payload,
  );
  const jobId = jobData.job_id;

  const shouldTrack = opts?.onJobCreated ? opts.onJobCreated(jobId) !== false : true;
  if (shouldTrack) {
    inFlightScenarioJobIds.add(jobId);
    try {
      sessionStorage.setItem(SCENARIO_JOB_STORAGE_KEY, jobId);
    } catch { /* ignore */ }
  }

  // ランダム生成でシェルが返ってきた場合は、ジョブ完了を待たず early-navigate するため
  // shell 情報をそのまま返す (ポーリングは呼び出し側がシェル単位で行う)。
  if (jobData.shell_scenarios && jobData.shell_scenarios.length > 0) {
    return { __shellFlow: true, jobId, shellScenarios: jobData.shell_scenarios };
  }

  return pollScenarioJob(jobId);
}

export async function pollScenarioJob(jobId: string): Promise<ScenarioResponse> {
  const POLL_INTERVAL = 3000;
  const MAX_POLLS = 200;
  let networkErrors = 0;
  const MAX_NETWORK_ERRORS = 10;

  for (let i = 0; i < MAX_POLLS; i++) {
    await waitForNextPoll(POLL_INTERVAL);

    let status: {
      job_id: string;
      status: 'pending' | 'processing' | 'completed' | 'failed';
      result?: ScenarioResponse;
      error?: string;
      error_code?: string;
    };

    try {
      const { data } = await apiClient.get<typeof status>(
        `/generation/scenarios/generate-status/${jobId}`,
      );
      status = data;
      networkErrors = 0; // リセット
    } catch {
      // ネットワークエラー（スリープ復帰、コールドスタート等）はリトライ
      networkErrors++;
      if (networkErrors >= MAX_NETWORK_ERRORS) {
        settleScenarioJob(jobId);
        clearScenarioJobResumeKeyIfSettled();
        throw new Error('サーバーとの通信に失敗しました。再度お試しください。');
      }
      continue;
    }

    if (status.status === 'completed' && status.result) {
      // 結果の消費側 (useScenarioGeneration) が復帰用キーを消す。
      settleScenarioJob(jobId);
      return status.result;
    }

    if (status.status === 'failed') {
      settleScenarioJob(jobId);
      clearScenarioJobResumeKeyIfSettled();
      const err: any = new Error(status.error || 'シナリオ生成に失敗しました');
      if (status.error_code) {
        err.response = { data: { error: status.error, error_code: status.error_code } };
      }
      throw err;
    }
  }

  settleScenarioJob(jobId);
  clearScenarioJobResumeKeyIfSettled();
  throw new Error('シナリオ生成がタイムアウトしました。再度お試しください。');
}

/** Seedance フレームモード + 顔あり の「セクション開始」シーンで true (faceSafe シーン)。
 *
 *  セクション開始 = シーン1 もしくは continues_previous_shot=false (場面転換) のシーン。
 *  これらのシーンではフォトリアルな顔含み画像を first_frame に渡せない
 *  (BytePlus face_upload_restricted)。代わりにフレームを絵コンテ用イラストとして生成し、
 *  reference_images (r2v) + 実写化指示で Seedance に動画を作らせ、その last_frame を
 *  以降の continues_previous_shot=true シーンに連鎖させる
 *  (docs/features/scene-face-illustration-frame-plan.md)。
 *
 *  この判定が true のとき:
 *   - フレーム生成はイラスト絵コンテとして実行 (D&D / 素材選択は非活性化のまま)
 *   - 動画生成 payload の first_frame / last_frame / image_material_id /
 *     last_frame_material_id を null にし、reference 経路で Seedance を呼ぶ
 *     (イラストフレームは illustration_frame_material_id で別送)
 *
 *  シーン単位の顔フラグ (scenes.has_visible_face・LLM 判定+ユーザー訂正) 駆動。
 *  欠損 (旧シナリオ) は顔あり扱い = 従来の一律 faceSafe 挙動を維持する。
 *  非写実スタイル (isStylized) はフレームに実在の顔が出ないため対象外。
 *  ユーザーが Veo / Kling を選んでいる場合は何も影響しない (Veo は
 *  person_generation='allow_adult' で顔含み first_frame を受け取れる)。
 *  最終判定はバックエンドでも再計算される (クライアント判定は UI/payload 構築用)。
 */
export function isFaceSafeSeedanceScene(
  videoModel: string | null | undefined,
  videoGenerationMode: string | null | undefined,
  avoidFaces: boolean,
  isFirstScene: boolean,
  continuesPreviousShot: boolean,
  hasVisibleFace: boolean | null | undefined,
  isStylized: boolean,
): boolean {
  if (videoGenerationMode !== 'frame') return false;
  if (avoidFaces) return false;
  if (isStylized) return false;
  if (!videoModel?.startsWith('seedance')) return false;
  // セクション開始 = シーン1 もしくは 場面転換
  if (!(isFirstScene || !continuesPreviousShot)) return false;
  // 顔フラグ欠損 (旧シナリオ) は顔あり扱い (安全側)
  return hasVisibleFace ?? true;
}


/** Seedance 2.0 Standard / Fast / Mini のフロントキー・API モデル ID 判定。 */
export function isSeedance20VideoModel(videoModel: string | null | undefined): boolean {
  const key = (videoModel ?? '').trim().toLowerCase();
  return ['seedance-2-standard', 'seedance-2-fast', 'seedance-2-mini'].includes(key)
    || key.startsWith('dreamina-seedance-2-0-');
}

/** Seedance 2.5 系のキー判定。機能の利用可否は各機能の metadata 判定を優先する。 */
export function isSeedance25VideoModel(videoModel: string | null | undefined): boolean {
  return (videoModel ?? '').startsWith('seedance-25');
}


/** この動画モデルのストーリーボード動画が「テキスト駆動」対象か。
 *
 *  テキスト駆動 = 絵コンテ画像を参照に渡さず、シーンテキストを正本に生成するモード
 *  (backend f67e942b: Seedance 2.5 は参照画像のスタイル転写が強く、絵コンテの
 *  画風・演出が完成動画へ転写されるため。イラスト/写真調・映像スタイルを問わず適用。
 *  絵コンテは人間の目視確認専用)。この条件ではストーリーボード未生成・未選択でも
 *  動画を生成できる。
 *
 *  正本は generation_models.metadata.storyboard_text_driven (DB 駆動)。未設定
 *  (行なし・キーなし・一覧未ロード) は Seedance 2.5 キー判定へフォールバックする。
 *  backend の resolve_storyboard_text_driven_model (video/prompt.py) と判定を
 *  揃えること (FE は絵コンテの生成モデル/ポイント表示、BE は添付除外を担う)。 */
export function isStoryboardTextDrivenModel(
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): boolean {
  const row = videoModels?.find((m) => m.model_code === videoModel);
  const flag = row?.metadata?.storyboard_text_driven;
  if (typeof flag === 'boolean') return flag;
  return isSeedance25VideoModel(videoModel);
}


/** この動画モデルのストーリーボード動画で、添付画像を「個別添付」するか。
 *
 *  個別添付 = ロゴ/選択素材を1枚のシート/統合タイルへ合成せず、そのまま複数枚の
 *  参照画像として渡すモード。参照画像上限が大きいモデル (Seedance 2.5 = 30枚) 向け。
 *  従来モデル (Veo=3枚等) は上限が小さいため、シート/タイル合成を維持する。
 *
 *  正本は generation_models.metadata.storyboard_individual_refs (DB 駆動)。未設定
 *  (行なし・キーなし・一覧未ロード) は Seedance 2.5 キー判定へフォールバックする。
 *  backend の resolve_storyboard_individual_refs_model (video/prompt.py) と判定を
 *  揃えること (FE はタイル合成スキップ/添付プレビュー UI、BE はロゴシート→個別ロゴ
 *  切替を担う)。 */
export function isStoryboardIndividualRefsModel(
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): boolean {
  const row = videoModels?.find((m) => m.model_code === videoModel);
  const flag = row?.metadata?.storyboard_individual_refs;
  if (typeof flag === 'boolean') return flag;
  return isSeedance25VideoModel(videoModel);
}


/** 「前の動画とつなげる」のモデル別つなぎ方式
 *  (docs/features/link-previous-video-multi-model-plan.md §5.1)。
 *   - 'frame_anchor_v2v' = Seedance 2.0 / 2.5 系 (ラストフレームの first frame アンカー + v2v 合流)
 *   - 'v2v_only' = MiniMax H3 (v2v 合流のみ。フレームアンカーとは相互排他)
 *   - 'extend' = Gemini Omni 1.1 (ネイティブ video extension。BE 生成ロジックは別タスクで
 *     現状は無視されるが、モデル能力の宣言値としては解決できる)
 *  backend の LINK_PREVIOUS_MODE_* (video/prompt.py) と同じ定数値にすること。 */
export const LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V = 'frame_anchor_v2v' as const;
export const LINK_PREVIOUS_MODE_V2V_ONLY = 'v2v_only' as const;
export const LINK_PREVIOUS_MODE_EXTEND = 'extend' as const;
export const LINK_PREVIOUS_MODES = [
  LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V,
  LINK_PREVIOUS_MODE_V2V_ONLY,
  LINK_PREVIOUS_MODE_EXTEND,
] as const;
export type LinkPreviousMode = typeof LINK_PREVIOUS_MODES[number];

/** この動画モデルの「前の動画とつなげる」つなぎ方式を解決する。
 *
 *  正本は generation_models.metadata.link_previous_mode (DB 駆動・既知3値の文字列)。
 *  backend の resolve_link_previous_mode (video/prompt.py) と全 enum 値で判定が
 *  一致すること (§5.1 の fail-closed 優先順位をそのまま反映):
 *
 *   1. `link_previous_mode` が既知の3値のいずれかならそれを採用する
 *   2. `link_previous_mode` が未設定で `last_frame_anchor` (旧フラグ) が bool の場合:
 *      true → 'frame_anchor_v2v' / **false → null (機能なし。Seedance 2.5 prefix に
 *      一致しても無効。旧 isLastFrameAnchorModel と同じ挙動を維持)**
 *   3. `link_previous_mode` と `last_frame_anchor` の**両方が未設定の場合のみ**
 *      Seedance 2.0 / 2.5 キー判定へフォールバックする
 *   4. `link_previous_mode` が既知3値以外の値 (未知の文字列等) の場合は
 *      **常に null (fail-closed)**。旧フラグ・Seedance 判定へは進まない
 */
export function resolveLinkPreviousMode(
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): LinkPreviousMode | null {
  const row = videoModels?.find((m) => m.model_code === videoModel);
  const metadata = row?.metadata;
  const mode = metadata?.link_previous_mode;
  if (mode !== undefined && mode !== null) {
    return (LINK_PREVIOUS_MODES as readonly unknown[]).includes(mode)
      ? (mode as LinkPreviousMode)
      : null; // 未知値は fail-closed (旧フラグ・Seedance 判定へは進まない)
  }

  const legacyFlag = metadata?.last_frame_anchor;
  if (typeof legacyFlag === 'boolean') {
    return legacyFlag ? LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V : null;
  }

  return isSeedance20VideoModel(videoModel) || isSeedance25VideoModel(videoModel)
    ? LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V : null;
}

/** components/generation/scenario の UI シナリオ/シーン形状
 *  (`./types` の `Scenario` / `Scenario['scenes'][number]`) から
 *  `features/scenarios/productionMode.ts` の入力形状へ変換する。UI 版は並び順キーが
 *  `order` (DB/productionMode 版は `order_number`)。§3.3 のデフォルト導出は
 *  scenario.scenes 全体 (直前の本編シーン探索) を必要とするため、シナリオ丸ごと
 *  変換する (docs/features/scene-location-and-production-mode-plan.md §3.3)。 */
type ExtendModeSceneLike = {
  order: number;
  location_key?: string | null;
  continues_previous_shot?: boolean | null;
  brand_cut_position?: 'start' | 'end' | null;
  link_previous_video?: boolean | null;
  video_input_mode?: string | null;
};
type ExtendModeScenarioLike = {
  frame_mode?: string | null;
  generation_metadata?: Record<string, unknown> | null;
  scenes?: ExtendModeSceneLike[] | null;
};

function toProductionModeScene(scene: ExtendModeSceneLike): SceneVideoInputModeSceneSource {
  return {
    order_number: scene.order,
    location_key: scene.location_key,
    continues_previous_shot: scene.continues_previous_shot,
    brand_cut_position: scene.brand_cut_position,
    link_previous_video: scene.link_previous_video,
    video_input_mode: scene.video_input_mode,
  };
}

function toProductionModeScenario(scenario: ExtendModeScenarioLike): SceneVideoInputModeScenarioSource {
  return {
    frame_mode: scenario.frame_mode,
    generation_metadata: scenario.generation_metadata,
    scenes: (scenario.scenes ?? []).map(toProductionModeScene),
  };
}

/** シーンの延長 (extend) 実効判定の起点 (P2 step 2。
 *  docs/features/scene-location-and-production-mode-plan.md §3.3/§3.3.1)。
 *
 *  `deriveSceneVideoInputMode(scenario, scene) === 'extend'` に、モデル対応ガード
 *  (`resolveLinkPreviousMode(...) !== null`) を外側で重ねたもの。モデルガードを
 *  純関数 (deriveSceneVideoInputMode) 側に入れないのは、延長の前提未充足時の
 *  縮退判定 (§3.4 preflight) が別責務のため — ここでは「対応モデルでなければ
 *  そもそも延長を検討しない」という、デフォルト導出の追加ゲートとしてのみ使う。
 *
 *  旧 `shouldDefaultLinkPreviousVideo` の「デフォルト ON」判定・
 *  `effectiveFrameLinkPreviousVideo` の起点条件 (continuesPreviousShot ×
 *  hasPrecedingScene) はいずれもこれに一本化される (P2 step 2)。
 *  deriveSceneVideoInputMode 自体は production_mode を見ないため、家系ごとの
 *  使い分けは呼び出し側が行う (storyboard は動画カードのアンカーシーン =
 *  カード先頭の本編シーンで判定する。`deriveLinkPreviousVideoSetting` 参照)。 */
export function deriveSceneExtendMode(
  scenario: ExtendModeScenarioLike,
  scene: ExtendModeSceneLike,
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): boolean {
  return (
    deriveSceneVideoInputMode(toProductionModeScenario(scenario), toProductionModeScene(scene)) === 'extend'
    && resolveLinkPreviousMode(videoModel, videoModels) !== null
  );
}

/** 「前の動画とつなげる」の読み出し時導出 (getSceneVideoSettings の単一経路。
 *  docs/features/link-previous-scene-continuity-default-plan.md §8 (第0弾: 参照画像) /
 *  §9 (第1弾: フレーム) / 第2弾: ストーリーボード)。
 *
 *  戻り値は sceneVideoSettings に載せる `linkPreviousVideo` の値:
 *   - 保存値 (`stored`。scenes.link_previous_video 由来の明示 ON/OFF) があり、かつ
 *     video_input_mode の明示値が無ければ保存値をそのまま返す
 *   - 保存値が未設定 (DB null = 一度も触られていない) なら `deriveSceneExtendMode`
 *     (場面継続 × location_key 一致 × 先行本編シーンあり × つなぎ方式が解決できる
 *     モデル) で true を導出する。導出できなければ undefined (= 未設定のまま OFF)
 *   - video_input_mode の明示値 ('frame' | 'extend' | 'auto') は旧 link_previous_video
 *     の明示値より常に優先する (§3.3.1)。旧トグルで false が残っているシーンに3択で
 *     'extend' を選ぶと、リロード後に stored=false が先勝ちして「3択は延長表示なのに
 *     延長実効 OFF」に乖離するバグの修正 (2026-08-31)。明示値が 'frame'/'auto'
 *     (またはモデル非対応で extend 不成立) のときは、stored に残る旧明示 ON も打ち消して
 *     false に揃える
 *
 *  家系別:
 *   - reference / frame: 第0弾・第1弾どおり
 *   - storyboard (第2弾・2026-09-14): 動画カードのアンカーシーン (= カード先頭の本編
 *     シーン) を `scene` に渡す。カードは複数シーンを跨ぐため「カード先頭シーンが直前
 *     カードの最終シーンから場面継続か」で判定する (§3)。storyboard は3択
 *     (video_input_mode) の対象外なので明示値の上書き分岐には実質入らない。
 *     自動分割 (useAutoVideoCardSegmentation) で生まれたカードは分割時に明示 ON が
 *     書き込まれるため、この導出は主に手動の「動画を追加」カードと既存データに効く
 *
 *  state へは書き込まず読み出し時導出に留める (モデル切替・場面継続の編集に追随させる)。 */
export function deriveLinkPreviousVideoSetting(params: {
  stored: boolean | undefined;
  scenario: ExtendModeScenarioLike | undefined;
  scene: ExtendModeSceneLike | undefined;
  videoModel: string | null | undefined;
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>;
}): boolean | undefined {
  const { stored, scenario, scene } = params;
  // 直前の本編シーンがモーショングラフィックス / 静止画なら延長は成立しない (レビュー反映 2026-09-28)。
  // 保存値 (旧 link_previous_video=true) が残っていても先に打ち消す。制作方式の導出 (deriveSceneVideoInputMode) も
  // 同じ規則で優先モードへ落とすので、AIおまかせの生成経路が延長を要求して backend の検証で拒否されることがなくなる
  if (scenario && scene && extendBlockedByPrevious(toProductionModeScenario(scenario).scenes ?? [], scene.order)) {
    return false;
  }
  const hasExplicitVideoInputMode = scene?.video_input_mode === 'frame'
    || scene?.video_input_mode === 'extend'
    || scene?.video_input_mode === 'auto';
  if (stored !== undefined && !hasExplicitVideoInputMode) return stored;
  if (!scenario || !scene) return stored;
  if (deriveSceneExtendMode(scenario, scene, params.videoModel, params.videoModels)) return true;
  if (hasExplicitVideoInputMode) return false;
  return stored;
}

/** シーンの実効入力方式 (P2 step 4。'frame' | 'extend' | 'auto')。
 *  `features/scenarios/productionMode.ts` の `deriveSceneVideoInputMode` の薄いラッパー
 *  (UI 型 → productionMode.ts の入力形状への変換込み)。
 *
 *  3択セレクタ (SceneFrameCarousel) の表示値 (video_input_mode が NULL のときに
 *  ラベルへ添える導出結果) に使う。モデル対応ガードは含まない
 *  (`deriveSceneVideoInputMode` 自体が持たない設計のため。§3.3.1 と同じ) —
 *  「延長」オプションの活性・非活性は呼び出し側が `resolveLinkPreviousMode` /
 *  `findPreviousVideoLastFrame` で別途判定すること。 */
export function deriveSceneVideoInputModeForUi(
  scenario: ExtendModeScenarioLike,
  scene: ExtendModeSceneLike,
): SceneVideoInputMode {
  return deriveSceneVideoInputMode(toProductionModeScenario(scenario), toProductionModeScene(scene));
}

/** シーンの入力方式3択 (SceneFrameCarousel) で「前の動画から延長」オプションを
 *  非活性にすべきか (P2 step 4・§3.2)。先行する本編シーンが無い、または選択中の
 *  動画モデルがつなぎ方式に対応していなければ非活性にする。`continues_previous_shot`
 *  は条件に含めない (§3.2: 3択は脚本属性から独立させる設計。前提未充足時の生成時
 *  preflight は後続 step)。 */
export function isExtendVideoInputModeOptionDisabled(
  hasPrecedingScene: boolean,
  linkPreviousMode: LinkPreviousMode | null,
  extendBlocked: ExtendBlockingInputMode | null = null,
): boolean {
  return !hasPrecedingScene || linkPreviousMode === null || extendBlocked !== null;
}

/** 「延長」非活性時の理由文言 (option の title に添える)。直前がモーショングラフィックス / 静止画の理由を最優先
 *  (brand-cut-motion-graphics-plan.md §10.4「場面継続」)。 */
export function extendVideoInputModeOptionDisabledReason(
  hasPrecedingScene: boolean,
  extendBlocked: ExtendBlockingInputMode | null = null,
): string {
  if (extendBlocked) return extendBlockedReasonText(extendBlocked);
  return hasPrecedingScene
    ? '選択中の動画モデルは延長に対応していません'
    : '先行するシーンがないため延長できません';
}

/** シーンの入力方式3択 (select) の生値から update_scene への PATCH payload を組み立てる
 *  (P2 step 4・§3.3.1)。P2 以降の書き込みは video_input_mode のみで、
 *  `link_previous_video` は新規に書かない。空文字は「自動 (NULL)」を表す。 */
export function buildVideoInputModePatchPayload(
  rawSelectValue: string,
): { video_input_mode: 'frame' | 'extend' | 'auto' | 'still' | 'motion' | null } {
  return { video_input_mode: (rawSelectValue || null) as 'frame' | 'extend' | 'auto' | 'still' | 'motion' | null };
}

/** シーンの「動画生成リクエスト実効値」(P2 step 3。'frame' | 'reference' | 'storyboard')。
 *  `features/scenarios/productionMode.ts` の `deriveEffectiveVideoGenerationMode` の
 *  薄いラッパー (UI 型 → productionMode.ts の入力形状への変換込み)。
 *
 *  §3.1 の設計どおり、実効入力方式 'extend' は 'reference' に丸められる
 *  (link_previous_video=true 相当を併せて送る対象、という宣言的な値)。この値は
 *  バックエンドの `resolve_face_safe_decision` (`mode_ok` 判定) 用の
 *  `payload.video_generation_mode` フィールドにのみ使う。'extend' → 'reference' の
 *  丸めはモデル対応 (resolveLinkPreviousMode) を見ない (deriveSceneVideoInputMode 自体が
 *  モデルを見ない設計のため) が、`resolve_face_safe_decision` の `is_face_safe` は
 *  `section_start` (= シーン1 or !continues_previous_shot) が前提条件になっており、
 *  'extend' 判定は必ず `continues_previous_shot===true` かつ先行シーンありを要求する
 *  ため `section_start` と両立しない。つまりこのフィールドの値が丸めの粗さで
 *  legacy 値と食い違っても、face_safe 判定への実害は無い (§9 分類 B)。
 *
 *  ⚠️ この粗さ (モデル未対応でも 'extend' → 'reference' に丸める) があるため、
 *  first_frame/last_frame の null 化や reference_images 構築の分岐には使わない
 *  こと — {@link deriveSceneInputFamily} を使う (frameLinkActive/deriveSceneExtendMode
 *  と組み合わせて使う設計)。 */
export function deriveSceneEffectiveVideoGenerationMode(
  scenario: ExtendModeScenarioLike,
  scene: ExtendModeSceneLike,
): EffectiveVideoGenerationMode {
  return deriveEffectiveVideoGenerationMode(toProductionModeScenario(scenario), toProductionModeScene(scene));
}

/** シーンの「入力系統」(P2 step 3。'frame' | 'reference' | 'storyboard')。シーンカードの
 *  frame vs reference 表示分岐・動画生成リクエストの reference_images 構築 /
 *  sceneImageUrl 必須判定など、家系そのもの (延長を除く) を左右する箇所の駆動元。
 *
 *  {@link deriveSceneEffectiveVideoGenerationMode} と異なり、実効入力方式 'extend' を
 *  丸めない: 'extend' は「延長するかどうか」という別軸 (継続性・モデル対応の両方に
 *  依存し、前提未充足なら延長せず通常どおり生成すべき) であり、「そのシーンが
 *  frame 系入力を使うか reference 系の参照収集を使うか」という家系そのものとは無関係
 *  (docs/features/scene-location-and-production-mode-plan.md §3.3 の「優先モード」が
 *  正本)。'extend' 判定はモデル対応をチェックしないため (deriveSceneVideoInputMode
 *  自体が production_mode/モデルを見ない設計)、'extend' をそのまま 'reference' へ丸めると
 *  「延長非対応モデル (例: Veo) を選んだ frame 優先シナリオの場面継続シーン」で
 *  reference_images が誤って組まれたり sceneImageUrl 必須チェックが誤って外れたりする
 *  (P1 で location_key が常時付与されるようになり、この組み合わせは現実に起こり得る)。
 *
 *  そのため 'extend' (明示・導出のいずれも) は常に優先モード
 *  (`derivePerSceneDefaultMode`) の家系へフォールバックする。延長の実効適用そのもの
 *  (first_frame/last_frame の null 化・previous_video_* フィールド送信) は、モデル対応を
 *  含めて判定済みの既存 `deriveSceneExtendMode`/`frameLinkActive`
 *  (`effectiveFrameLinkPreviousVideo`) が別軸として引き続き担う。
 *
 *  video_input_mode が全 NULL の現在、この関数は常に
 *  `derivePerSceneDefaultMode(scenario)` (シナリオ単位の優先モード。旧
 *  `deriveVideoGenerationMode(scenario)` と同じ frame/reference 判定) と全シーンで
 *  一致する (extend 分類の有無によらず、'extend' も優先モードへ丸められるため)。 */
export function deriveSceneInputFamily(
  scenario: ExtendModeScenarioLike,
  scene: ExtendModeSceneLike,
): EffectiveVideoGenerationMode {
  const scenarioSource = toProductionModeScenario(scenario);
  if (deriveProductionMode(scenarioSource) === 'storyboard') return 'storyboard';
  const explicit = scene.video_input_mode;
  if (explicit === 'frame') return 'frame';
  if (explicit === 'auto') return 'reference';
  // 'still' (静止画をそのまま使う) は動画を生成しないので家系を持たない。フレーム生成 UI
  // (フレームタブ・フレーム生成ボタン) を出さない reference 側に寄せる。
  if (explicit === 'still') return 'reference';
  // 'motion' (モーショングラフィックス) も動画 AI を呼ばないので家系を持たない。still と同じく reference 側に寄せる
  // (フレーム生成 UI を出さない。生成ボタンは SceneVideoBox が isMotionScene で切り替える)。
  if (explicit === 'motion') return 'reference';
  // 'extend' (明示 or 導出) は家系を持たないため優先モードへフォールバックする。
  return derivePerSceneDefaultMode(scenarioSource) === 'frame' ? 'frame' : 'reference';
}

/** 「前の動画とつなげる」の未設定時デフォルト ON 判定
 *  (docs/features/link-previous-scene-continuity-default-plan.md)。
 *
 *  **レガシー専用 (P2 step 2 で本流から退役)**: 現在の呼び出し箇所
 *  (`getSceneVideoSettings` / `useFrameHandlers.isFrameLinkPreviousActive`) は
 *  `deriveSceneExtendMode` (§3.3/§3.3.1 準拠。location_key の矛盾検知を追加で持つ)
 *  へ置き換え済み。挙動比較・過去の参照用にここへ残す
 *  (docs/features/scene-location-and-production-mode-plan.md §9 分類A)。
 *
 *  scenes.link_previous_video が未設定 (DB null = 一度も触られていない) のシーンに限り、
 *  参照画像モード / フレームモード (§9 step-1 で拡張) × 場面継続 (continues_previous_shot) ×
 *  つなぎ方式が解決できるモデル × 先行本編シーンあり のときデフォルト ON を導出する。
 *  明示 ON/OFF (保存値 true/false) は常に優先し、導出しない。ブランドカット
 *  (タイトル/エンドカード) のシーンも対象外。
 *
 *  frame モードへの拡張 (§9 step-1 項目2): reference モードの既存条件は変更しない。
 *  storyboard モードは第1弾までは対象外 (未設定時 OFF) だったが、第2弾 (2026-09-14) で
 *  `deriveLinkPreviousVideoSetting` 側がカード先頭シーンで導出するようになった
 *  (このレガシー関数は storyboard を引き続き false で返す)。 */
export function shouldDefaultLinkPreviousVideo(params: {
  /** sceneVideoSettings の保存値。undefined = 未設定 (DB null) のときだけ導出する */
  storedLinkPreviousVideo: boolean | undefined;
  /** シナリオの動画生成モード (deriveVideoGenerationMode の結果) */
  videoGenerationMode: string | null;
  continuesPreviousShot: boolean | null | undefined;
  brandCutPosition?: string | null;
  /** findPreviousVideoLastFrame(...).hasPrecedingScene (先行する本編シーンの有無) */
  hasPrecedingScene: boolean;
  videoModel: string | null | undefined;
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>;
}): boolean {
  if (params.storedLinkPreviousVideo !== undefined) return false;
  if (params.videoGenerationMode !== 'reference' && params.videoGenerationMode !== 'frame') return false;
  if (!params.continuesPreviousShot) return false;
  if (params.brandCutPosition === 'start' || params.brandCutPosition === 'end') return false;
  if (!params.hasPrecedingScene) return false;
  return resolveLinkPreviousMode(params.videoModel, params.videoModels) !== null;
}

/** フレームモードにおける「前の動画とつなげる (延長)」の実効判定
 *  (docs/features/link-previous-scene-continuity-default-plan.md §9 step-1 項目3。
 *  再レビュー案A採用。P2 step 2 で起点を deriveSceneExtendMode へ一本化)。
 *
 *  【フレームモード専用】。参照画像・ストーリーボードモードの既存経路 (第0弾で出荷済み)
 *  はこのヘルパーへ置き換えず、従来のゲート (mode 判定 + トグル値) のまま維持する。
 *
 *  保存値 `scenes.link_previous_video` (= `linkPreviousVideoSetting`。
 *  `getSceneVideoSettings` の読み出し時導出後の値で、未設定なら場面継続×対応モデルで
 *  true が導出済み) はモード・モデルをまたいで残るため、参照画像モードで明示 ON の
 *  ままフレームモードへ切り替えた場合や非対応モデルへ切り替えた場合に、保存値だけを
 *  見てフレームを null 化すると「延長は適用されないのにフレームも送られない」入力欠落
 *  が起こる。表示・フレーム生成スキップ・payload の null 化・課金見積り・生成ボタン
 *  制御のすべてがこの関数を参照することで、保存値はモデル切替・場面転換への変更で
 *  消さずに (条件を満たさない間だけ実効 OFF)、対応モデル・場面継続へ戻したときに
 *  明示選択が復元されるようにする。
 *
 *  起点条件 (旧: continuesPreviousShot === true && hasPrecedingScene) は
 *  `deriveSceneExtendMode(scenario, scene, ...)` へ置き換えた。§3.3 のデフォルト
 *  導出条件を包含する上、location_key の矛盾検知 (§3.3 「延長せず優先モードへ」) も
 *  併せて効くようになる (旧関数にはこの検知が無かった)。 */
export function effectiveFrameLinkPreviousVideo(params: {
  videoGenerationMode: string | null | undefined;
  scenario: ExtendModeScenarioLike;
  scene: ExtendModeSceneLike;
  /** getSceneVideoSettings 経由で読み出した (導出済みの) linkPreviousVideo 値 */
  linkPreviousVideoSetting: boolean | undefined;
  videoModel: string | null | undefined;
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>;
}): boolean {
  return (
    params.videoGenerationMode === 'frame'
    && deriveSceneExtendMode(params.scenario, params.scene, params.videoModel, params.videoModels)
    && params.linkPreviousVideoSetting === true
  );
}

/** この動画モデルが「前の動画と繋げる」(ラストフレーム first frame アンカー) 対象か。
 *
 *  前の動画カードのラストフレームを通常の参照画像として合流させ、プロンプト文
 *  `@Image N is the first frame.` で役割宣言する方式 (Seedance 2.5 公式ガイドの
 *  Keyframe Anchors。docs/features/scenario-previous-video-link-plan.md)。
 *
 *  実態は `resolveLinkPreviousMode(videoModel, videoModels) ===
 *  LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V` の薄いラッパー (§5.1 でモデル別つなぎ方式が
 *  3種に拡張されたため、正本は resolveLinkPreviousMode へ移った)。「つなげる機能自体の
 *  有無」を見たい呼び出し元は `resolveLinkPreviousMode(...) !== null` を使うこと
 *  (v2v_only / extend も機能ありに含めるため、このラッパーでは判定できない)。
 *  backend の resolve_last_frame_anchor_model (video/prompt.py) と判定を揃えること。 */
export function isLastFrameAnchorModel(
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): boolean {
  return resolveLinkPreviousMode(videoModel, videoModels) === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V;
}

/** 「前の動画とつなげる」の v2v 入力秒数の最低計上値 (§5.3)。
 *
 *  正本は generation_models.metadata.link_previous_min_input_seconds (数値)。
 *  未設定時のフォールバックはモード別:
 *   - frame_anchor_v2v → 4 (Seedance provider の
 *     SEEDANCE_20/25_REFERENCE_VIDEO_CONSTRAINTS.min_billed_input_seconds のミラー。
 *     BytePlus の最低トークン消費という provider 固有制約であり、他モデルには適用しない)
 *   - それ以外 (v2v_only 等) → 0 (H3 には reference_video_constraints 自体が無く、
 *     最低計上という概念がない。backend billing.py も入力秒をそのまま ceil するだけ)
 */
export function resolveLinkPreviousMinInputSeconds(
  mode: LinkPreviousMode | null,
  videoModel: string | null | undefined,
  videoModels?: Array<{ model_code: string; metadata: Record<string, unknown> | null }>,
): number {
  const row = videoModels?.find((m) => m.model_code === videoModel);
  const raw = row?.metadata?.link_previous_min_input_seconds;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return raw;
  return mode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V ? 4 : 0;
}

/** 「前の動画とつなげる」の v2v 合算課金見積りにおける入力秒数
 *  (= BE resolve_input_video_seconds と同じ式: `max(最低計上, ceil(入力秒))`)。
 *  SceneItem.tsx (バッジ表示) と useVideoHandlers.ts (生成前残高チェック) の
 *  両方でこの関数を通すことで式のズレを防ぐ。
 *
 *  inputDurationSeconds が不明 (null/undefined/0以下。素材の尺がまだ DB に反映
 *  されていない等) な場合、fallbackInputDurationSeconds (正の値のときのみ採用) を
 *  代わりに使う。frame_anchor_v2v (Seedance 2.0 / 2.5) は最低計上4秒で下支えされるため
 *  実害は小さいが、v2v_only (MiniMax H3) は最低計上が無く、不明時に 0 のまま見積もると
 *  バッジ・生成前残高チェックの両方が入力分まるごと過小表示になる
 *  (docs/features/link-previous-video-multi-model-plan.md §5.3 のレビュー指摘)。
 *  fallback も無ければ従来どおり 0 として扱う。 */
export function resolveLinkPreviousInputSeconds(
  minBilledInputSeconds: number,
  inputDurationSeconds: number | null | undefined,
  fallbackInputDurationSeconds?: number | null,
): number {
  const hasKnownInput = typeof inputDurationSeconds === 'number' && inputDurationSeconds > 0;
  const hasFallback = typeof fallbackInputDurationSeconds === 'number' && fallbackInputDurationSeconds > 0;
  const resolvedInput = hasKnownInput
    ? inputDurationSeconds
    : (hasFallback ? fallbackInputDurationSeconds : 0);
  return Math.max(minBilledInputSeconds, Math.ceil(resolvedInput));
}

/** {@link resolveLinkPreviousInputSeconds} の fallbackInputDurationSeconds を解決する。
 *
 *  参照元シーン (§10 B案選択時はそのシーン、未選択時は直前シーン = prevSceneKey) の
 *  「動画カードの尺設定」(sceneVideoSettings.duration) を、実測尺が取れないときの
 *  近似値として使う。ストーリーボード・参照モードいずれの動画カードも
 *  `${scenarioId}-scene-${order}` キーで尺設定を持つ (getSceneVideoSettings は未設定でも
 *  既定値 { duration: 4, ... } を返すため、動画カードなら必ず数値が取れる)。あくまで
 *  近似であり、正確な課金は BE が素材の実測尺で確定する。
 *
 *  SceneItem.tsx (バッジ表示) と useVideoHandlers.ts (生成前残高チェック) の両方から
 *  同じ引数 (sourceScene?.order / prevInfo.prevSceneKey) で呼び、必ず同じ値になる
 *  ようにすること。 */
export function resolveLinkPreviousInputFallbackSeconds(
  scenarioId: string,
  sourceSceneOrder: number | null | undefined,
  prevSceneKey: string | null | undefined,
  getSceneVideoSettings: (sceneKey: string) => { duration?: number } | undefined,
): number | null {
  const key = typeof sourceSceneOrder === 'number'
    ? `${scenarioId}-scene-${sourceSceneOrder}`
    : (prevSceneKey || null);
  if (!key) return null;
  const duration = getSceneVideoSettings(key)?.duration;
  return typeof duration === 'number' && duration > 0 ? duration : null;
}

/** 「前の動画とつなげる」の v2v 合流がこのシーンで発動可能か (§5.1 のモード別)。
 *
 *  - frame_anchor_v2v: 前の動画が生成済み **かつ** ラストフレーム素材が解決できること
 *    (first frame アンカーに使うため必須)
 *  - v2v_only / extend: 前の動画が生成済みであれば足りる (フレーム素材自体を使わない。
 *    v2v_only は §5.3、extend は将来 BE 実装時にネイティブ延長のベース動画として使う)
 */
export function isLinkPreviousV2vReady(
  mode: LinkPreviousMode | null,
  videoGenerated: boolean,
  frameMaterialId: string | null | undefined,
  selectedSourceAvailable: boolean = false,
): boolean {
  if (!mode) return false;
  if (mode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V) return videoGenerated && !!frameMaterialId;
  // v2v_only は参照元の明示指定 (B案) があれば直前の動画の生成を待たずに繋げられる
  // (BE も指定シーンのみを検証する)。extend は常に直前を延長するため直前の生成が必須。
  if (mode === LINK_PREVIOUS_MODE_V2V_ONLY) return videoGenerated || selectedSourceAvailable;
  return videoGenerated;
}

/** 延長元シーンの候補 (order が直前) の key を返す。`findPreviousVideoLastFrame` と
 *  異なり、動画が生成済みかどうかを問わない (docs/features/
 *  scene-location-and-production-mode-plan.md §3.4 preflight: 「延長元シーンが
 *  いま生成中」を判定するには、動画がまだ無い preceding シーンも対象にする必要が
 *  あるため)。ブランドカットは対象外。先行する本編シーンが無ければ null。 */
export function findNearestPrecedingSceneKey(
  scenarioId: string,
  scenes: PreviousVideoSceneLike[],
  sceneOrder: number,
): string | null {
  const preceding = scenes
    .filter((s) => (
      s.brand_cut_position !== 'start'
      && s.brand_cut_position !== 'end'
      && s.order < sceneOrder
    ))
    .sort((a, b) => b.order - a.order);
  const nearest = preceding[0];
  return nearest ? `${scenarioId}-scene-${nearest.order}` : null;
}

/** 「延長元の動画が更新されています」バッジの判定 (§3.4 レビュー反映・P2 step 6-3)。
 *
 *  自シーンの生成済み動画の snapshot (`generation_metadata.previous_video_source_*`。
 *  BE `apply_previous_video_frame_payload` / `persistence.py` が延長系生成の成功時に
 *  書き込む。P2 step 6-1) と、現在の延長元シーン (`previous_video_source_scene_id` で
 *  特定) の `video_material_id` を突き合わせる。
 *
 *  - snapshot 自体が無い (延長未適用・旧データ) → false (誤検知防止)
 *  - 延長元シーンが特定できない (削除済み等) → false (判定材料が無いため)
 *  - 延長元シーンの動画がまだ無い → false (「更新」ではなく「未生成」なので別の
 *    導線 (preflight) の対象)
 */
export function isLinkPreviousSourceStale(
  scene: { generation_metadata?: Record<string, unknown> | null },
  scenes: Array<{ id?: string; video_material_id?: string }>,
): boolean {
  const meta = scene.generation_metadata;
  if (!meta || typeof meta !== 'object') return false;
  const sourceSceneId = (meta as Record<string, unknown>).previous_video_source_scene_id;
  const sourceMaterialId = (meta as Record<string, unknown>).previous_video_source_material_id;
  if (typeof sourceSceneId !== 'string' || !sourceSceneId) return false;
  if (typeof sourceMaterialId !== 'string' || !sourceMaterialId) return false;
  const sourceScene = scenes.find((s) => s.id === sourceSceneId);
  if (!sourceScene || !sourceScene.video_material_id) return false;
  return sourceScene.video_material_id !== sourceMaterialId;
}


/** findPreviousVideoLastFrame が読むシーンの最小構造 (structural typing)。 */
type PreviousVideoSceneLike = {
  order: number;
  brand_cut_position?: 'start' | 'end' | null;
  video_material_id?: string;
  last_frame_material_id?: string;
  /** 生成動画 (現在のテイク) の末尾フレーム素材。あればユーザー枠より優先する */
  video_last_frame_material_id?: string;
  video_duration_seconds?: number | null;
  id?: string;
  generation_metadata?: Record<string, unknown>;
  video_input_mode?: string | null;
};

/** 「前の動画とつなげる」の参照元 (@Video1) に選べる候補 (§10 B案)。
 *  このカードのアンカーシーンより前の本編シーンのうち、生成済み動画を持つもの。
 *  ラベルは担当範囲 (generation_metadata.scene_range) があれば「シーンs〜e」、
 *  無ければ「シーンN」。尺 (video_duration_seconds) はコスト目安の表示と
 *  v2v 見積りに使う。order 昇順。 */
export function listPreviousVideoSources(
  scenes: PreviousVideoSceneLike[],
  sceneOrder: number,
): Array<{ sceneId: string; order: number; label: string; durationSeconds: number | null }> {
  return scenes
    .filter((s) => (
      s.brand_cut_position !== 'start'
      && s.brand_cut_position !== 'end'
      && s.order < sceneOrder
      && !!s.video_material_id
      && !!s.id
      // モーショングラフィックス / 静止画の動画は実写の参照元にならない (BE も同じ理由で fail-fast する)
      && s.video_input_mode !== 'motion' && s.video_input_mode !== 'still'
    ))
    .sort((a, b) => a.order - b.order)
    .map((s) => {
      const range = (s.generation_metadata as { scene_range?: { start?: number; end?: number } } | undefined)?.scene_range;
      const start = Number(range?.start);
      const end = Number(range?.end);
      const label = Number.isFinite(start) && Number.isFinite(end) && end > start
        ? `シーン${start}〜${end}`
        : `シーン${s.order}`;
      return {
        sceneId: s.id as string,
        order: s.order,
        label,
        durationSeconds: (
          typeof s.video_duration_seconds === 'number' && s.video_duration_seconds > 0
            ? s.video_duration_seconds
            : null
        ),
      };
    });
}

/** @メンションの「他シーンの動画」節の1行 (docs/features/scene-video-frame-materials-visibility-plan.md §2.2)。 */
export type SceneFrameMentionSourceRow = {
  sceneId: string;
  order: number;
  /** 表示名 (例: 「シーン1の動画」)。 */
  label: string;
  videoMaterialId: string;
  /** 行の動画サムネイル。シーン行に動画サムネイル列は無いため、動画の終わり絵 → 最後のフレーム枠 →
   *  最初のフレームの順で代替する。 */
  thumbnailUrl: string | null;
};

/** listSceneFrameMentionSourcesが読むシーンの最小構造。 */
type SceneFrameMentionSourceSceneLike = {
  id?: string;
  order: number;
  video_material_id?: string | null;
  video_input_mode?: string | null;
  brand_cut_position?: 'start' | 'end' | null;
  image_url?: string | null;
  last_frame_url?: string | null;
  video_last_frame_url?: string | null;
};

/**
 * @メンションのポップアップに出す「他シーンの動画」の母集団 (新規挿入用・§2.2)。
 * 自分以外の本編シーンで生成済み動画があり、モーショングラフィックス / 静止画でないもの
 * (listPreviousVideoSourcesと同じ除外。ブランドカットも除く)。シーン順。シーンの前後は問わない。
 */
export function listSceneFrameMentionSources(
  scenario: { scenes?: SceneFrameMentionSourceSceneLike[] | null } | null | undefined,
  currentSceneId: string | null | undefined,
): SceneFrameMentionSourceRow[] {
  return (scenario?.scenes ?? [])
    .map((s) => ({ scene: s, videoId: s.video_material_id || null }))
    .filter(({ scene, videoId }) => (
      !!scene.id
      && scene.id !== currentSceneId
      && scene.brand_cut_position !== 'start' && scene.brand_cut_position !== 'end'
      && !!videoId
      && scene.video_input_mode !== 'motion' && scene.video_input_mode !== 'still'
    ))
    .sort((a, b) => a.scene.order - b.scene.order)
    .map(({ scene, videoId }) => ({
      sceneId: scene.id as string,
      order: scene.order,
      label: `シーン${scene.order}の動画`,
      videoMaterialId: videoId as string,
      thumbnailUrl: scene.video_last_frame_url || scene.last_frame_url || scene.image_url || null,
    }));
}

/** 「前の動画と繋げる」の対象になる「前の動画」を解決する。
 *
 *  前の動画 = このカードのアンカーシーンより order が小さい本編シーン
 *  (ブランドカット除外) のうち、生成済み動画を持つ最も近いシーン。生成直後で
 *  シナリオ再取得前のカードは sceneVideoMaterialId (ローカル state) で拾う。
 *
 *  - hasPrecedingScene: 先行する本編シーンが 1 つでもあるか (false ならトグル自体を出さない)
 *  - videoGenerated: 前の動画が生成済みか (false なら非活性 + 「まだ生成されていません」)
 *  - lastFrameMaterialId: 生成済みでも BE のフレーム抽出/保存前は null になり得る
 *    (その場合も非活性にする)
 *  - prevVideoDurationSeconds: 前の動画の尺 (v2v 拡張の消費ポイント見積り用。
 *    scene.video_duration_seconds 由来で、不明なら null)。 */
export function findPreviousVideoLastFrame(
  scenarioId: string,
  scenes: PreviousVideoSceneLike[],
  sceneOrder: number,
  sceneVideoMaterialId?: Record<string, string | null | undefined>,
): {
  hasPrecedingScene: boolean;
  videoGenerated: boolean;
  lastFrameMaterialId: string | null;
  prevSceneKey: string | null;
  prevVideoDurationSeconds: number | null;
} {
  const preceding = scenes.filter((s) => (
    s.brand_cut_position !== 'start'
    && s.brand_cut_position !== 'end'
    && s.order < sceneOrder
  ));
  if (preceding.length === 0) {
    return { hasPrecedingScene: false, videoGenerated: false, lastFrameMaterialId: null, prevSceneKey: null, prevVideoDurationSeconds: null };
  }
  const withVideo = preceding
    .filter((s) => !!(s.video_material_id || sceneVideoMaterialId?.[`${scenarioId}-scene-${s.order}`]))
    .sort((a, b) => b.order - a.order);
  const prev = withVideo[0];
  if (!prev) {
    return { hasPrecedingScene: true, videoGenerated: false, lastFrameMaterialId: null, prevSceneKey: null, prevVideoDurationSeconds: null };
  }
  return {
    hasPrecedingScene: true,
    videoGenerated: true,
    // 連鎖のアンカーは「前の動画が実際に終わった絵」。生成動画の末尾フレーム
    // (video_last_frame_material_id。フレームモードでは scenes.last_frame_material_id に
    // 書かれないため別フィールド) を優先し、無ければユーザーの「最後のフレーム」枠
    lastFrameMaterialId: prev.video_last_frame_material_id || prev.last_frame_material_id || null,
    prevSceneKey: `${scenarioId}-scene-${prev.order}`,
    prevVideoDurationSeconds: (
      typeof prev.video_duration_seconds === 'number' && prev.video_duration_seconds > 0
        ? prev.video_duration_seconds
        : null
    ),
  };
}


/** 既知の error_code に対応するユーザー向け文言。未知のコードは null を返す。
 *  AsyncJobError (.error_code) と axios エラー (.response.data.error_code) の両方を duck-typing で見る。
 *  (docs/features/seedance-mini-concurrency-queueing.md §9.1/9.5)
 */
export function friendlyJobErrorMessage(error: unknown): string | null {
  const e = error as {
    error_code?: string;
    response?: { data?: { error_code?: string; required?: number; available?: number } };
  };
  const code = e?.error_code ?? e?.response?.data?.error_code;
  switch (code) {
    case 'MODEL_TEMPORARILY_UNAVAILABLE':
      return '現在このモデルが混雑または一時停止中です。少し時間をおいて再度お試しください。';
    case 'JOB_EXPIRED':
      return '混雑により生成がタイムアウトしました。ポイントは返還されています。少し時間をおいて再度お試しください。';
    case 'INSUFFICIENT_CREDITS': {
      // 数値はバックエンド (backend/utils/credit_errors.py) が同梱する。
      // AsyncJobError 経由など数値が無い場合は文言のみにフォールバックする。
      const { required, available } = e?.response?.data ?? {};
      const detail = typeof required === 'number' && typeof available === 'number'
        ? `（必要 ${formatPoints(required)}pt / 残高 ${formatPoints(available)}pt）`
        : '';
      return `ポイントが不足しています${detail}。ポイントを購入してから再度お試しください。`;
    }
    default:
      return null;
  }
}

/** ポイント数を表示用に整える (825 → '825'、12.5 → '12.5')。 */
function formatPoints(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

/** 動画URLからメタデータを読み込み、再生尺(秒)を取得する。
 *
 *  ブランドカット(エンドカード等)に動画素材を紐付けた直後、タイムライン表示と
 *  エクスポート尺を実尺に合わせるために使う。素材の duration が DB 未保存でも
 *  クライアント側で確定できる。取得失敗時 (URL 不正・CORS・タイムアウト) は null。
 *  尺(duration) はクロスオリジンでも読めるため CORS でタイントされない。
 */
export function probeVideoDurationSeconds(
  url: string | undefined | null,
  timeoutMs = 8000,
): Promise<number | null> {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    let settled = false;
    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      try { video.load(); } catch { /* ignore */ }
    };
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    video.onloadedmetadata = () => {
      const d = video.duration;
      finish(Number.isFinite(d) && d > 0 ? d : null);
    };
    video.onerror = () => finish(null);
    video.src = url;
  });
}

// 選択した素材からASINを抽出
export function extractAsinsFromAssets(assets: any[]): string[] {
  const asinSet = new Set<string>();
  assets.forEach((asset) => {
    if (asset.asin) {
      if (Array.isArray(asset.asin)) {
        asset.asin.forEach((a: string) => {
          if (a) asinSet.add(a);
        });
      } else if (typeof asset.asin === 'string') {
        asinSet.add(asset.asin);
      }
    }
  });
  return Array.from(asinSet);
}

// ---------------------------------------------------------------------------
// Seedance 実在人物フィルタ (face_privacy_flagged) の表示ヘルパー
// backend face_privacy._classify_flag_kind の kind と対で使う。
// ---------------------------------------------------------------------------

/** kind → ユーザー向け呼称 (backend の _FLAG_KIND_JA と同じ規約) */
export const FACE_PRIVACY_KIND_LABELS: Record<string, string> = {
  logo: 'ブランドロゴ画像',
  product_tile: '商品画像',
  product_image: '商品画像',
  selected_asset: '選択素材の画像',
  storyboard: '絵コンテ画像',
  illustration_frame: '絵コンテ用イラスト',
  frame: 'フレーム画像',
  reference_video: '参照動画',
  reference_audio: '参照音声',
  reference_image: '参考画像',
};

/** flagged item の表示名。backend 由来の label を優先し、無ければ kind の呼称 */
export function facePrivacyItemLabel(item: { kind?: string; label?: string }): string {
  return item.label || FACE_PRIVACY_KIND_LABELS[item.kind || ''] || '参照画像';
}

/**
 * 署名付き URL の照合用にクエリ (署名・期限) を落とす。
 * 同じストレージオブジェクトなら発行タイミングが違ってもパスは一致する。
 */
export function stripUrlQuery(url: string | undefined | null): string {
  return (url || '').split('?')[0];
}

/**
 * シーン上書き > シナリオ既定 の順で「実際に使う動画モデル」を解決する。空文字と
 * 'random' (抽選) は無視し、どちらも無ければ既定モデルへフォールバックする。
 * SceneItem の実効モデル判定と useAutoVideoCardSegmentation の再分割判定で共有する。
 */
export const resolveUsableVideoModel = (...candidates: Array<string | null | undefined>): string => {
  const usable = candidates
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find((value) => value.length > 0 && value !== 'random');
  return usable || DEFAULT_VIDEO_MODEL;
};

/**
 * 「既存の素材を挿入」で追加したブランドカット (タイトル/エンドカード) の映像欄の定型文。
 * BE の attach_brand_cut_from_material (scene_generator.py) が scenes.visual に書き込む値と
 * 一致させること (scenes に由来を示す専用カラムが無いため、これを目印にしている)。
 */
export const BRAND_CUT_FROM_MATERIAL_VISUAL = '素材から追加したブランドカット';

/**
 * 既存素材をそのままクリップとして使うブランドカットか。AI で作ったタイトル/エンドカードは
 * フレーム生成・動画生成で作り直せるが、素材から追加したものは素材 (動画/静止画) が
 * そのまま出力になるので、フレーム生成・動画生成・シーン詳細の編集 UI は意味を持たない。
 * (AI 生成のブランドカットも動画生成後は video_material_id を持つため、紐付け動画の
 * 有無では区別できない)
 */
export function isBrandCutFromMaterial(scene: { brand_cut_position?: string | null; visual?: string | null }): boolean {
  return !!scene.brand_cut_position && scene.visual === BRAND_CUT_FROM_MATERIAL_VISUAL;
}
